import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { PreviewType } from 'src/app/features/articles/models/articles.models';
import { Country } from 'src/app/features/customers/models/customer-address.models';
import { RuntimeConfigService } from './runtime-config.service';

@Injectable({ providedIn: 'root' })
export class ApiService {
    private readonly runtimeConfigService = inject(RuntimeConfigService);

    readonly baseUrl = {
        api: this.runtimeConfigService.getApiBasePath(),
        apiV2: this.runtimeConfigService.getApiV2BasePath(),
        applicationId: this.runtimeConfigService.getApplicationId(),
    };

    readonly pathUrl = {
        // User Management
        users: () => `${this.baseUrl.api}/users`,
        selectorsUsers: (query = '') => `${this.baseUrl.api}/users?$select=id, displayName${query}`,
        userById: (id: string) => `${this.baseUrl.api}/users/${id}`,
        userRights: (id: string) => `${this.baseUrl.api}/users/${id}/rights`,
        updateUserRights: (id: string) => `${this.baseUrl.api}/users/${id}/rights`,
        currentUserRights: () => `${this.baseUrl.api}/authenticateduser/rights`,
        userRoles: (id: string) => `${this.baseUrl.api}/users/${id}/roles`,
        userRoleById: (userId: string, roleId: string) => `${this.baseUrl.api}/users/${userId}/roles/${roleId}`,
        userRightsById: (userId: string, rightId: string) => `${this.baseUrl.api}/users/${userId}/rights/${rightId}`,

        // Roles Management
        roles: () => `${this.baseUrl.api}/roles`,
        roleById: (roleId: string) => `${this.baseUrl.api}/roles/${roleId}`,
        createRole: () => `${this.baseUrl.api}/roles`,

        // Rights Management
        rights: () => `${this.baseUrl.api}/rights`,
        rightById: (id: string) => `${this.baseUrl.api}/rights/${id}`,

        // Authentication & User Profile
        authenticatedUser: () => `${this.baseUrl.api}/authenticateduser`,
        userAvatar: () => `${this.baseUrl.api}/authenticateduser/avatar`,
        userProfile: () => `${this.baseUrl.api}/authenticateduser/profile`,

        // File upload endpoints
        uploadAvatar: () => `${this.baseUrl.api}/authenticateduser/avatar`,
        tempFileUpload: () => `${this.baseUrl.api}/fileupload`,

        // Customers Management
        customers: (query = '') => `${this.baseUrl.api}/customers${query}`,
        customerById: (id: string) => `${this.baseUrl.api}/customers/${id}`,
        blockEntity: () => `${this.baseUrl.api}/block`,
        entityBlocks: (query = '') => `${this.baseUrl.api}/block${query}`,
        entityBlockById: (id: string) => `${this.baseUrl.api}/block/${id}`,

        // Required Actions (Action Management)
        requiredActions: (query = '') => `${this.baseUrl.api}/required-actions${query}`,
        requiredActionCheckStatus: (id: string) => `${this.baseUrl.api}/required-actions/${id}/check-status`,
        customerAvatar: (id: string) => `${this.baseUrl.api}/customers/${id}/avatar`,
        customerProjectReference: (customerId: string) => `${this.baseUrl.api}/customers/${customerId}/project-reference-numbers`,
        customerDeliveryDate: (id: string) => `${this.baseUrl.api}/customers/${id}/delivery-date`,

        // Customer Contacts Management
        customerContacts: (customerId: string) => `${this.baseUrl.api}/customers/${customerId}/contacts`,
        customerContactById: (customerId: string, contactId: string) => `${this.baseUrl.api}/customers/${customerId}/contacts/${contactId}`,
        customerContactAvatar: (customerId: string, contactId: string) =>
            `${this.baseUrl.api}/customers/${customerId}/contacts/${contactId}/avatar`,

        // Customer Addresses Management
        customerAddresses: (customerId: string) => `${this.baseUrl.api}/customers/${customerId}/addresses`,
        customerAddressById: (customerId: string, addressId: string) => `${this.baseUrl.api}/customers/${customerId}/addresses/${addressId}`,

        // Customer Bank Accounts Management
        customerBankAccounts: (customerId: string) => `${this.baseUrl.api}/customers/${customerId}/bankaccounts`,
        customerBankAccountById: (customerId: string, bankAccountId: string) =>
            `${this.baseUrl.api}/customers/${customerId}/bankaccounts/${bankAccountId}`,

        // Documents Management
        documents: (endpoint: string, id: string) => `${this.baseUrl.api}/${endpoint}/${id}/documents`,
        documentById: (endpoint: string, id: string, documentId: string) => `${this.baseUrl.api}/${endpoint}/${id}/documents/${documentId}`,
        documentContent: (endpoint: string, id: string, documentId: string) =>
            `${this.baseUrl.api}/${endpoint}/${id}/documents/${documentId}/content`,

        // Customer Billing Management
        customerBilling: (customerId: string) => `${this.baseUrl.api}/customers/${customerId}/billing`,

        // General Management
        generalAvailableVat: () => `${this.baseUrl.api}/general/available-vat`,
        generalStates: (country: Country) => `${this.baseUrl.api}/general/country/${country}/states`,

        // Settings
        generalSettings: () => `${this.baseUrl.api}/configurations/settings`,
        settingByKey: (key: string) => `${this.baseUrl.api}/configurations/settings/${key}`,
        notificationFlags: () => `${this.baseUrl.api}/configurations/notificationFlags`,
        actionFlags: () => `${this.baseUrl.api}/configurations/actionFlags`,
        featureFlags: () => `${this.baseUrl.api}/configurations/featureFlags`,
        featureFlagByKey: (key: string) => `${this.baseUrl.api}/configurations/featureflags/${key}`,
        numberFormats: () => `${this.baseUrl.api}/configurations/numberFormats`,
        validateNumberFormats: (template: string) => `${this.baseUrl.api}/configurations/numberFormats/validate/${template}`,

        // Grid Layouts Management
        gridLayout: (key: string) => `${this.baseUrl.api}/gridlayouts/${key}`,
        gridLayoutsV2: (query = '') => `${this.baseUrl.apiV2}/gridlayouts${query}`,
        gridLayoutV2Tab: (key: string, tabKey: string) =>
            `${this.baseUrl.apiV2}/gridlayouts/${encodeURIComponent(key)}/tabs/${encodeURIComponent(tabKey)}`,

        // Shipping methods Management
        shippingMethods: (query = '') => `${this.baseUrl.api}/shipping-methods${query}`,
        shippingMethodById: (id: string) => `${this.baseUrl.api}/shipping-methods/${id}`,

        // Payment conditions Management
        paymentConditions: (query = '') => `${this.baseUrl.api}/payment-conditions${query}`,
        paymentConditionById: (id: string) => `${this.baseUrl.api}/payment-conditions/${id}`,

        // Raw Materials Management
        rawMaterials: (query = '') => `${this.baseUrl.api}/raw-materials${query}`,
        rawMaterialsThickness: () => `${this.baseUrl.api}/raw-materials/thicknesses`,
        baseMaterials: (query = '') => `${this.baseUrl.api}/base-materials${query}`,

        // Articles Management
        articles: (query = '') => `${this.baseUrl.api}/articles${query}`,
        articlesById: (id: string) => `${this.baseUrl.api}/articles/${id}`,
        articlePriceLists: (articleId: string, query = '') => `${this.baseUrl.api}/articles/${articleId}/pricelists${query}`,
        articlePriceListById: (articleId: string, priceListId: string) => `${this.baseUrl.api}/articles/${articleId}/pricelists/${priceListId}`,
        articlesIndividualStocks: (articleId: string) => `${this.baseUrl.api}/articles/${articleId}/individualstocks`,
        previewImage: (articleId: string, previewType: PreviewType) => `${this.baseUrl.api}/articles/${articleId}/preview/${previewType}`,

        // Drawing Management
        supportedFiles: () => `${this.baseUrl.api}/drawing-import/supported-files`,
        importAnalysis: () => `${this.baseUrl.api}/drawing-import`,
        analysisResult: (importId: string) => `${this.baseUrl.api}/drawing-import/${importId}`,
        preview2d: (importId: string) => `${this.baseUrl.api}/drawing-import/${importId}/preview-2d`,
        preview3d: (importId: string) => `${this.baseUrl.api}/drawing-import/${importId}/preview-3d`,

        // Customer Orders Management
        customerOrders: (query = '') => `${this.baseUrl.api}/customer-orders${query}`,
        customerOrderById: (orderId: string) => `${this.baseUrl.api}/customer-orders/${orderId}`,
        customerOrderSources: () => `${this.baseUrl.api}/customer-orders/sources`,
        customerOrderSalesReps: () => `${this.baseUrl.api}/customer-orders/sales-representatives`,
        customerOrderUpdatePositionDate: (orderId: string) => `${this.baseUrl.api}/customer-orders/${orderId}/positions`,

        // Customer Order Positions Management
        customerOrderPositionsById: (orderId: string, query = '') => `${this.baseUrl.api}/customer-orders/${orderId}/positions${query}`,
        customerOrderPositionById: (orderId: string, positionId: string) =>
            `${this.baseUrl.api}/customer-orders/${orderId}/positions/${positionId}`,
        customerOrderDispositionSuggestion: (orderId: string) => `${this.baseUrl.api}/customer-orders/${orderId}/disposition-suggestion`,
        customerOrderDisposition: (orderId: string) => `${this.baseUrl.api}/customer-orders/${orderId}/disposition`,
        customerOrderPositionDispositionSuggestion: (orderId: string, positionId: string) =>
            `${this.baseUrl.api}/customer-orders/${orderId}/positions/${positionId}/disposition-suggestion`,
        customerOrderPositionDisposition: (orderId: string, positionId: string) =>
            `${this.baseUrl.api}/customer-orders/${orderId}/positions/${positionId}/disposition`,

        // Blanket Orders Management
        blanketOrders: (query = '') => `${this.baseUrl.api}/blanket-orders${query}`,
        blanketOrderById: (id: string) => `${this.baseUrl.api}/blanket-orders/${id}`,
        blanketOrderCustomerOrders: (id: string) => `${this.baseUrl.api}/blanket-orders/${id}/customer-orders`,
        blanketOrderPositionsOpen: (query = '') => `${this.baseUrl.api}/blanket-order-positions/open${query}`,

        // Production Orders Management
        productionOrders: (query = '') => `${this.baseUrl.api}/production-orders${query}`,
        productionOrderById: (productionOrderId: string) => `${this.baseUrl.api}/production-orders/${productionOrderId}`,
        cancelProductionOrder: (productionOrderId: string) => `${this.baseUrl.api}/production-orders/${productionOrderId}/cancel`,
        workplans: (query = '') => `${this.baseUrl.api}/workplans${query}`,

        // Stocks Management
        stockReservations: (query = '') => `${this.baseUrl.api}/stock-reservations${query}`,
        stockReservationById: (id: string) => `${this.baseUrl.api}/stock-reservations/${id}`,

        // Deliveries Management
        deliveries: (query = '') => `${this.baseUrl.api}/deliveries${query}`,
        deliveryById: (deliveryId: string) => `${this.baseUrl.api}/deliveries/${deliveryId}`,
        deliveryPositions: (deliveryId: string) => `${this.baseUrl.api}/deliveries/${deliveryId}/positions`,
        deliveryTransportItems: (deliveryId: string) => `${this.baseUrl.api}/deliveries/${deliveryId}/transport-items`,
        deliveryTransportItemById: (deliveryId: string, transportItemId: string) =>
            `${this.baseUrl.api}/deliveries/${deliveryId}/transport-items/${transportItemId}`,
        // deliveryByIdWithQuery: (deliveryId: string, query = '') => `${this.baseUrl.api}/deliveries/${deliveryId}${query}`,

        // Invoices Management
        invoices: (query = '') => `${this.baseUrl.api}/invoices${query}`,
        invoiceById: (invoiceId: string) => `${this.baseUrl.api}/invoices/${invoiceId}`,
        invoiceCancellation: (invoiceId: string) => `${this.baseUrl.api}/invoices/${invoiceId}/cancellation`,
        creditNotes: () => `${this.baseUrl.api}/invoices/credit-notes`,
        creditNoteById: (invoiceId: string) => `${this.baseUrl.api}/invoices/credit-notes/${invoiceId}`,
        invoicePositionsByInvoiceId: (invoiceId: string) => `${this.baseUrl.api}/invoices/${invoiceId}/positions`,
        invoicePositionById: (invoiceId: string, positionId: string) => `${this.baseUrl.api}/invoices/${invoiceId}/positions/${positionId}`,
        invoicePaymentsByInvoiceId: (invoiceId: string) => `${this.baseUrl.api}/invoices/${invoiceId}/payments`,
        invoicePaymentById: (invoiceId: string, paymentId: string) => `${this.baseUrl.api}/invoices/${invoiceId}/payments/${paymentId}`,
        downPaymentInvoices: () => `${this.baseUrl.api}/invoices/down-payment-invoices`,
        downPaymentInvoiceById: (invoiceId: string) => `${this.baseUrl.api}/invoices/down-payment-invoices/${invoiceId}`,

        // Print Layouts Management
        printLayouts: (query = '') => `${this.baseUrl.api}/print-layouts${query}`,
        printLayoutsById: (type: string, id: string, query = '') => `${this.baseUrl.api}/print-layouts/${type}/${id}${query}`,
        printLayoutContent: (type: string, id: string) => `${this.baseUrl.api}/print-layouts/${type}/${id}/content`,
        printLayoutsByType: (type: string) => `${this.baseUrl.api}/print-layouts/${type}`,

        // Reports Management
        generateReports: (resultType: string) => `${this.baseUrl.api}/reports/generate?resultType=${resultType}`,

        // operation
        updateMESVersion: () => `${this.baseUrl.api}/service/mes-version`,
    };

    /**
     * Create HTTP parameters from an object
     * @param parameters Object with key-value pairs
     * @returns HttpParams instance
     */
    params(parameters: Record<string, any>): HttpParams {
        let params = new HttpParams();
        Object.keys(parameters).forEach(key => {
            if (parameters[key] !== null && parameters[key] !== undefined) {
                params = params.append(key, parameters[key]);
            }
        });
        return params;
    }

    /**
     * Handle HTTP errors with specific error codes and messages
     * @param httpError The HTTP error response
     * @returns Observable with error message
     */
    handleError(httpError: HttpErrorResponse): Observable<string> {
        // Handle specific error codes
        if (httpError?.error?.errorCode) {
            const errorCode = httpError.error.errorCode;

            switch (errorCode) {
                case 'USER_NOT_FOUND':
                    return throwError(() => new Error('User not found'));
                case 'ROLE_NOT_FOUND':
                    return throwError(() => new Error('Role not found'));
                case 'RIGHT_NOT_FOUND':
                    return throwError(() => new Error('Right not found'));
                case 'INSUFFICIENT_PERMISSIONS':
                    return throwError(() => new Error('Insufficient permissions'));
                case 'USER_ALREADY_EXISTS':
                    return throwError(() => new Error('User already exists'));
                case 'ROLE_ALREADY_EXISTS':
                    return throwError(() => new Error('Role already exists'));
                case 'INVALID_USER_DATA':
                    return throwError(() => new Error('Invalid user data'));
                case 'INVALID_ROLE_DATA':
                    return throwError(() => new Error('Invalid role data'));
                case 'UNAUTHORIZED':
                    return throwError(() => new Error('Unauthorized access'));
                case 'FORBIDDEN':
                    return throwError(() => new Error('Access forbidden'));
                default:
                    return throwError(() => new Error(httpError.error.message || 'An unknown error occurred'));
            }
        }

        // Handle client-side errors
        if (httpError.error instanceof ErrorEvent) {
            console.error('Client-side error occurred:', httpError.error.message);
            return throwError(() => new Error('A client-side error occurred. Please try again.'));
        }

        // Handle server-side errors
        if (httpError.error?.error) {
            console.error(`Server error: ${httpError.status}`, httpError.error.error);
            return throwError(() => new Error(httpError.error.error.message || 'Server error occurred'));
        }

        // Handle HTTP status codes
        switch (httpError.status) {
            case 400:
                return throwError(() => new Error('Bad request. Please check your input.'));
            case 401:
                return throwError(() => new Error('Unauthorized. Please log in again.'));
            case 403:
                return throwError(() => new Error('Forbidden. You do not have permission to perform this action.'));
            case 404:
                return throwError(() => new Error('Resource not found.'));
            case 409:
                return throwError(() => new Error('Conflict. The resource already exists or is in use.'));
            case 422:
                return throwError(() => new Error('Validation error. Please check your input.'));
            case 500:
                return throwError(() => new Error('Internal server error. Please try again later.'));
            case 503:
                return throwError(() => new Error('Service unavailable. Please try again later.'));
            default:
                return throwError(() => new Error(`HTTP ${httpError.status}: An error occurred. Please try again.`));
        }
    }

    /**
     * Get query string from parameters object
     * @param params Object with query parameters
     * @returns Query string
     */
    buildQueryString(params: Record<string, any>): string {
        const queryParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
            if (params[key] !== null && params[key] !== undefined) {
                queryParams.append(key, params[key]);
            }
        });
        return queryParams.toString();
    }

    /**
     * Build URL with query parameters
     * @param baseUrl Base URL
     * @param params Query parameters
     * @returns Complete URL with query string
     */
    buildUrl(baseUrl: string, params?: Record<string, any>): string {
        if (!params) return baseUrl;
        const queryString = this.buildQueryString(params);
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }
}
